/**
 * Meal Actions Menu Component
 *
 * 3-dot menu that provides Edit/Adjust/Swap options for meals.
 * Features click-outside detection, keyboard navigation, and accessibility support.
 *
 * @module components/meal-plans/MealActionsMenu
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/hooks/useTheme';
import type { MealPlanFrontend, MealAction } from '@/types/mealPlan';

/**
 * Props for MealActionsMenu component
 */
export interface MealActionsMenuProps {
  /** The meal to perform actions on */
  meal: MealPlanFrontend['mealPlanData']['meals'][0];
  /** ID of the meal plan containing this meal */
  mealPlanId: string;
  /** Callback when an action is selected */
  onActionSelect?: (action: MealAction) => void;
  /** Whether the menu is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Position of the menu button (for styling) */
  position?: 'card' | 'dialog';
  /** Optional handler to trigger a PDF export */
  onExportMeal?: () => Promise<void> | void;
  /** Whether to show edit option (default true) */
  showEdit?: boolean;
  /** Whether to show adjust option (default true) */
  showAdjust?: boolean;
  /** Whether to show swap option (default true) */
  showSwap?: boolean;
  /** Whether to show export option (default true if onExportMeal provided) */
  showExport?: boolean;
  /** Whether to show add-to-shopping-list option (default false) */
  showAddToShoppingList?: boolean;
  /** Handler to add meal ingredients to shopping list */
  onAddToShoppingList?: () => Promise<void> | void;
}

/**
 * Meal Actions Menu Component
 */
export const MealActionsMenu: React.FC<MealActionsMenuProps> = ({
  onActionSelect,
  disabled = false,
  className = '',
  position = 'card',
  onExportMeal,
  showEdit = true,
  showAdjust = true,
  showSwap = true,
  showExport,
  showAddToShoppingList = false,
  onAddToShoppingList,
}) => {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isExportingMeal, setIsExportingMeal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Handle tooltip visibility
  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true);
      // Show tooltip after a short delay
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 500);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowTooltip(false);
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleActionSelect = (action: MealAction) => {
    setIsOpen(false);
    onActionSelect?.(action);
  };

  const handleKeyDown = (event: React.KeyboardEvent, action: MealAction) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActionSelect(action);
    }
  };

  const handleExportClick = async () => {
    if (!onExportMeal || isExportingMeal) {
      return;
    }

    setIsOpen(false);
    setShowTooltip(false);
    setIsExportingMeal(true);
    try {
      await onExportMeal();
    } catch (exportError) {
      console.error('Failed to export meal PDF:', exportError);
    } finally {
      setIsExportingMeal(false);
    }
  };

  const positionClass = position === 'card' ? 'meal-actions-menu-card' : 'meal-actions-menu-dialog';

  return (
    <div className={`${positionClass} ${className}`} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={() => {
          setIsOpen(!isOpen);
          setShowTooltip(false);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={() => !disabled && setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        aria-label="More options"
        aria-haspopup="true"
        aria-expanded={isOpen}
        style={{
          padding: '8px',
          borderRadius: '6px',
          border: isDark
            ? `1px solid ${isHovered || isFocused ? '#6b7280' : '#4b5563'}`
            : `1px solid ${isHovered || isFocused ? '#9ca3af' : '#d1d5db'}`,
          backgroundColor: isDark
            ? (isHovered || isFocused ? '#4b5563' : '#374151')
            : (isHovered || isFocused ? '#f9fafb' : 'white'),
          color: isDark ? '#f9fafb' : '#111827',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          outline: isFocused ? `2px solid ${isDark ? '#60a5fa' : '#3b82f6'}` : 'none',
          outlineOffset: '2px',
          transform: isHovered && !disabled ? 'scale(1.05)' : 'scale(1)',
          boxShadow: isHovered && !disabled
            ? isDark
              ? '0 2px 8px rgba(0, 0, 0, 0.3)'
              : '0 2px 8px rgba(0, 0, 0, 0.1)'
            : 'none'
        }}
      >
        {/* 3-dot vertical icon */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      </button>

      {/* Tooltip */}
      {showTooltip && !isOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
            padding: '6px 12px',
            backgroundColor: isDark ? '#1f2937' : '#111827',
            color: '#f9fafb',
            fontSize: '12px',
            fontWeight: '500',
            borderRadius: '6px',
            whiteSpace: 'nowrap',
            boxShadow: isDark
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.4)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
            zIndex: 10000,
            pointerEvents: 'none',
          }}
        >
          More options
          {/* Tooltip arrow */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${isDark ? '#1f2937' : '#111827'}`,
            }}
          />
        </div>
      )}

      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            minWidth: '160px',
            backgroundColor: isDark ? '#1f2937' : 'white',
            border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: isDark
              ? '0 10px 15px -3px rgba(0, 0, 0, 0.4)'
              : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          {showEdit && (
            <div
              role="menuitem"
              tabIndex={0}
              onClick={() => handleActionSelect('edit')}
              onKeyDown={(e) => handleKeyDown(e, 'edit')}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: isDark ? '#f9fafb' : '#111827',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Edit icon */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span>Edit Meal</span>
            </div>
          )}

          {showAdjust && (
            <div
              role="menuitem"
              tabIndex={0}
              onClick={() => handleActionSelect('adjust')}
              onKeyDown={(e) => handleKeyDown(e, 'adjust')}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: isDark ? '#f9fafb' : '#111827',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Adjust icon (sparkles/wand) */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4" />
              </svg>
              <span>Adjust Meal</span>
            </div>
          )}

          {showSwap && (
            <div
              role="menuitem"
              tabIndex={0}
              onClick={() => handleActionSelect('swap')}
              onKeyDown={(e) => handleKeyDown(e, 'swap')}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: isDark ? '#f9fafb' : '#111827',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Swap icon (refresh/arrows) */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              <span>Swap Meal</span>
            </div>
          )}

          {showAddToShoppingList && onAddToShoppingList && (
            <div
              role="menuitem"
              tabIndex={0}
              onClick={() => {
                setIsOpen(false);
                void onAddToShoppingList();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setIsOpen(false);
                  void onAddToShoppingList();
                }
              }}
              style={{
                padding: '12px 16px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                color: isDark ? '#f9fafb' : '#111827',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                opacity: disabled ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Add to shopping list icon (plus) */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              <span>Add to shopping list</span>
            </div>
          )}

          {onExportMeal && (showExport ?? true) && (
            <div
              role="menuitem"
              tabIndex={0}
              onClick={handleExportClick}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  void handleExportClick();
                }
              }}
              style={{
                padding: '12px 16px',
                cursor: isExportingMeal ? 'wait' : 'pointer',
                fontSize: '14px',
                color: isDark ? '#f9fafb' : '#111827',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                opacity: isExportingMeal ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Export icon (download) */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>{isExportingMeal ? 'Exporting…' : 'Export PDF'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MealActionsMenu;